import { useEffect } from 'react';
import {
  createFileRoute,
  redirect,
  Outlet,
  useCanGoBack,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { IconChevronLeft, IconCheck, IconX } from '@tabler/icons-react';
import { AppShell, useMinWidth } from '@tupai/shell';
import { capture } from '@tupai/analytics/client';
import { useAuthStore } from '@/store/auth-store';
import { useLinksStore } from '@/store/links-store';
import { useChildLink } from '@/lib/use-child-link';
import { startLinksPolling } from '@/lib/links-polling';
import { useWorksheetStore } from '@/store/worksheet-store';
import { generateWorksheetPdf } from '@/lib/pdf';
import { TopBar } from '@/components/top-bar';
import { BottomNav } from '@/components/bottom-nav';
import { WorksheetPrintable } from '@/components/worksheet-printable';
import {
  AssignmentWorksheetPrintable,
  ASSIGNMENT_WORKSHEET_PRINT_ROOT_ID,
} from '@/components/assignment-worksheet-printable';
import AssignmentStatusRailCard from '@/components/rail/assignment-status-rail-card';
import ChildrenRailCard from '@/components/rail/children-rail-card';
import ExamChildRailCard from '@/components/rail/exam-child-rail-card';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/_protected')({
  // A deliberate logout goes to /login, any other signed-out visit to /start (TUP-1757).
  beforeLoad: () => {
    const { token, signedOut } = useAuthStore.getState();
    if (!token) throw redirect({ to: signedOut ? '/login' : '/start', replace: true });
  },
  component: RootLayout,
});

// WP2's rail-contents-of-record, one card set per tab root — AppShell only
// mounts any of this from `lg`, and each card self-fetches, so no page
// changes were needed to grow a rail under it. /news still gets none: the
// rail rule (docs/DESIGN.md's "Tablet and desktop") says the rail holds only
// what the phone screen already shows, and there is no "recent worksheets"
// data source in this app to summarise there — downloads aren't recorded
// client-side, and inventing one would break that rule rather than serve it.
// /exam gets ExamChildRailCard instead (WP1): the child's syllabus, which the
// phone screen's own Syllabus picker already exposes, so this stays inside
// the rule rather than an exception to it. /profile's rail carries children
// only — the plan lives in the column at every width (ProfilePage's "Your
// plan" section), since that copy carries things a rail card can't (the
// billing owner's Manage subscription row, the pending-downgrade line, the
// free-tier gate body, and the household student name in the plan name).
function railFor(pathname: string) {
  // '/children' lost its rail card in WP1: the household desk's list pane
  // (ChildListPane, from `lg`) is the "+"/link entry point now, so a
  // duplicate rail card would just be a second way to do the same thing.
  if (pathname === '/practice') return <AssignmentStatusRailCard />;
  if (pathname === '/exam') return <ExamChildRailCard />;
  if (pathname === '/profile') return <ChildrenRailCard />;
  return undefined;
}

// Full-screen flows with their own X/back + no BottomNav underneath (DESIGN.md
// back contract) — the paywall plus WP4's two onboarding screens, riding on
// AppShell's fullscreen variant, which now supplies the 448px column itself.
// Exact-match is fine: none of the three is parameterised.
const FULLSCREEN = ['/paywall', '/onboarding/details', '/onboarding/plans'];

// One parameterised header for every "detail" screen off the four main tabs:
// Profile, the live AI summary, the child hub, and (WP4) the hub's
// sub-routes — back button + centred title, no bottom nav. Collapses what
// used to be three near-identical local headers (SummaryHeader/
// ChildHubHeader/ProfileHeader) into one, since the only things that ever
// differed between them were the title text and back target.
function DetailHeader() {
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const location = useLocation();
  const { linkId } = useParams({ strict: false });
  const { logout } = useAuthStore();
  const { t } = useTranslation('common');

  const link = useChildLink(linkId, { approvedOnly: false });
  const childName = link?.other_party_name || link?.other_party_phone || t('childFallback');

  const isProfile = location.pathname === '/profile';
  const isNotifications = location.pathname === '/notifications';

  let title = childName;
  let backToHub = false;

  if (isProfile) {
    title = t('profileAndSettings');
  } else if (isNotifications) {
    title = t('notifications');
  } else {
    // /children/:linkId/(reports|homework|activity) — every sub-route names
    // itself and backs out to the hub, not the children list (PHUB-004/028).
    // The hub itself and both report views render their own headers (band /
    // report-headline page-head), so DetailHeader never sees those paths.
    const rest = location.pathname.replace(/^\/children\/[^/]+/, '');
    if (rest === '/reports') {
      title = t('childReports', { childName });
      backToHub = true;
    } else if (rest === '/homework') {
      title = t('childHomework', { childName });
      backToHub = true;
    } else if (rest === '/activity') {
      title = t('childActivity', { childName });
      backToHub = true;
    }
  }

  const handleLogout = () => {
    navigate({ to: '/login', replace: true });
    logout();
  };

  // Every other detail screen has one parent to back out to. Notifications
  // doesn't: the bell that opens it sits on all four main tabs, so a fixed
  // destination would send three of them somewhere they didn't come from.
  // Router history, falling back to /children when there's nowhere to pop to
  // (a deep link straight in).
  const handleBack = () => {
    if (!isNotifications) {
      if (backToHub && linkId) void navigate({ to: '/children/$linkId', params: { linkId } });
      else void navigate({ to: '/children' });
      return;
    }
    if (canGoBack) router.history.back();
    else void navigate({ to: '/children', replace: true });
  };

  // Prototype page-head recipe: back + left-aligned 18px/700 title. The
  // `safe-area-top` inset and the bottom hairline both moved to AppShell's
  // header wrapper (WP1) — this root only supplies its own padding now.
  return (
    <div className="bg-surface pl-1 pr-4 py-2 flex-shrink-0 flex items-center gap-1">
      <button
        aria-label={t('back')}
        onClick={handleBack}
        className="w-11 h-11 rounded-pill flex items-center justify-center text-content transition-all hover:bg-bg cursor-pointer shrink-0"
      >
        <IconChevronLeft size={20} stroke={2.5} />
      </button>
      <p className="flex-1 text-lg font-bold text-content truncate">{title}</p>
      {isProfile && (
        <button
          onClick={handleLogout}
          className="text-sm font-bold text-error hover:opacity-70 transition-opacity cursor-pointer animate-fade-in shrink-0"
        >
          {t('logOut')}
        </button>
      )}
    </div>
  );
}

function RootLayout() {
  const token = useAuthStore((s) => s.token);
  const { toast, refreshParentLinks, refreshMyCode } = useLinksStore();
  const {
    printExam,
    printQuestions,
    printLang,
    clearPrint,
    printAssignment,
    printAssignmentQuestions,
    printAssignmentLang,
    clearAssignmentPrint,
  } = useWorksheetStore();
  const location = useLocation();

  // Behaviour switch, not layout — same contract as TopBar's own
  // `useMinWidth('lg')` for the bell. Decides which chrome the /children
  // area gets (sidebar + spanning TopBar vs DetailHeader/no nav); the CSS
  // itself is all in AppShell's `wide` prop and the pages' own `lg:` classes.
  const isLg = useMinWidth('lg');

  const isProfile = location.pathname === '/profile';
  const isNotifications = location.pathname === '/notifications';
  const isSummary = location.pathname.startsWith('/summary/');
  // Any /children/:id... path — the hub itself and every WP4 sub-route
  // (reports, a single report, homework, activity). Below `lg` these lose
  // TopBar/BottomNav same as always; from `lg` the household desk (WP1)
  // wants the opposite — sidebar nav + spanning TopBar, no rail — since the
  // list pane sits where the rail used to and the hub's own band supplies
  // the per-child identity chrome the phone got from DetailHeader/back.
  const isChildDetail = /^\/children\/[^/]+/.test(location.pathname);
  const isChildArea = location.pathname === '/children' || isChildDetail;
  const isDetail = isSummary || (isChildDetail && !isLg) || isNotifications;
  const isFullscreen = FULLSCREEN.includes(location.pathname);
  // The four main tabs AND Profile get BottomNav — WP1 makes Profile a
  // sixth sidebar item from `lg` (navHiddenBelowLg below), so it no longer
  // drops the nav outright the way isDetail/isFullscreen routes still do.
  const showNav = !isDetail && !isFullscreen;
  // Detail screens that own their header (prototype): the hub's and Profile's
  // back buttons live inside their blue identity band, and both report views
  // put the fetched report headline in their own page-head — only those pages
  // know the title. Profile joined the list in WP7's rebuild. Every
  // `/children/...` entry here is `lg`-conditional (WP1/WP2): below `lg` the
  // page's own band/page-head is the only header; from `lg` the desk puts
  // TopBar above it, and the report/homework routes are dialogs over the hub
  // whose own header is the dialog's, not the screen's (canvas Main, A-Report,
  // A-Homework — all three show the parent header row).
  const ownsHeader =
    isProfile ||
    isFullscreen ||
    isSummary ||
    (!isLg &&
      (/^\/children\/[^/]+$/.test(location.pathname) ||
        /^\/children\/[^/]+\/reports\/[^/]+$/.test(location.pathname) ||
        /^\/children\/[^/]+\/homework\/[^/]+$/.test(location.pathname)));

  useEffect(() => {
    refreshMyCode();
    // Refreshes now, then every 30s (paused while hidden, backing off on
    // failure) so a student accepting/rejecting/being claimed by another
    // parent reflects here without waiting for navigation.
    return startLinksPolling(refreshParentLinks);
  }, []);

  // PDF generation — fires when printExam + printQuestions are populated.
  // `worksheet_downloaded` fires here, not on the Download button tap
  // (WorksheetPage/PracticePage): the button click only starts a fetch that
  // can still fail (quota/upgrade gate, network) before a PDF ever exists —
  // this is the actual point a worksheet was downloaded. Exam papers carry
  // neither a chapter nor a specific child, so both properties stay
  // undefined for this path.
  useEffect(() => {
    if (!printExam || printQuestions.length === 0) return;
    let cancelled = false;
    let succeeded = false;
    generateWorksheetPdf(printExam, printLang)
      .then(() => {
        succeeded = true;
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (cancelled) return;
        clearPrint();
        if (succeeded) capture('worksheet_downloaded', {});
      });
    return () => {
      cancelled = true;
    };
  }, [printExam, printQuestions, printLang, clearPrint]);

  // Same as above, for assignment (chapter practice) worksheets — chapterId/
  // linkId are threaded through from PracticePage via the store (see
  // worksheetStore.ts's printAssignment shape).
  useEffect(() => {
    if (!printAssignment || printAssignmentQuestions.length === 0) return;
    let cancelled = false;
    let succeeded = false;
    generateWorksheetPdf(
      { title: printAssignment.chapterTitle },
      printAssignmentLang,
      ASSIGNMENT_WORKSHEET_PRINT_ROOT_ID
    )
      .then(() => {
        succeeded = true;
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (cancelled) return;
        const { chapterId, linkId } = printAssignment;
        clearAssignmentPrint();
        if (succeeded) {
          capture('worksheet_downloaded', { chapter_id: chapterId, link_id: linkId });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [printAssignment, printAssignmentQuestions, printAssignmentLang, clearAssignmentPrint]);

  // The redirect itself happens in beforeLoad, re-run by main.tsx when the token goes.
  if (!token) return null;

  return (
    <>
      {printExam && printQuestions.length > 0 && (
        <WorksheetPrintable exam={printExam} questions={printQuestions} lang={printLang} />
      )}

      {printAssignment && printAssignmentQuestions.length > 0 && (
        <AssignmentWorksheetPrintable
          chapterTitle={printAssignment.chapterTitle}
          studentName={printAssignment.studentName}
          questions={printAssignmentQuestions}
          lang={printAssignmentLang}
        />
      )}

      {/* h-dvh (student web build opens on laptops too, per DESIGN.md's
          "Tablet and desktop") and the max-w-md/shadow column both now live
          inside `@tupai/shell`'s AppShell (WP1) — this file only supplies
          which slots get which content. `scroll={false}` because every
          parent page renders its own `flex-1 overflow-y-auto` region. */}
      <AppShell
        variant={isFullscreen ? 'fullscreen' : 'default'}
        tone="surface"
        // fullscreen only (ignored otherwise): the three FULLSCREEN routes
        // already carry their own `safe-area-top` on the pages themselves.
        safeTop={false}
        // canvas P5-Paywall-1280: the fullscreen main is max-w-md everywhere,
        // but /paywall widens to a 960px column from `xl` so
        // pages/PaywallPage.tsx's `xl:grid-cols-3` seat-tier grid fits.
        className={location.pathname === '/paywall' ? 'xl:max-w-[960px]' : undefined}
        header={
          ownsHeader ? undefined : isProfile || (isChildDetail && !isLg) || isNotifications ? (
            <DetailHeader />
          ) : (
            <TopBar />
          )
        }
        nav={showNav ? <BottomNav /> : undefined}
        rail={railFor(location.pathname)}
        navHiddenBelowLg={isProfile}
        // WP1: the desk lays out its own pane + fill-width detail, so it
        // never wants AppShell's 640/672px centred column.
        wide={isChildArea}
        scroll={false}
      >
        <Outlet />
      </AppShell>

      {/* fixed, not absolute: canvas P0-Toast calls for the toast centred on
          the WINDOW, not the column/rail — same as the student app's toasts —
          so this anchors to the viewport as a sibling of AppShell, not to its
          `relative` main region. */}
      {toast && (
        <div
          className={`fixed left-1/2 -translate-x-1/2 bottom-24 lg:bottom-8 z-60 px-4 py-2.5 rounded-pill shadow-lg text-sm font-bold text-surface flex items-center gap-2 animate-fade-in ${
            toast.type === 'success' ? 'bg-green' : 'bg-error'
          }`}
        >
          {toast.type === 'success' ? (
            <IconCheck size={14} stroke={2.5} />
          ) : (
            <IconX size={14} stroke={2.5} />
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </>
  );
}
